import { createElement, useState } from 'react';
import { Linking, Pressable, useColorScheme } from 'react-native';
import { View, YStack } from 'tamagui';
import { Play } from 'lucide-react-native';

import { AuthenticatedImage } from '@/components/authenticated-image';
import { Text } from '@/components/text';
import type { Attachment } from '@/lib/api';
import { borderWidth, darkColors, lightColors } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

// A shared YouTube/Vimeo video as a click-to-play card (web). Nothing from
// the provider loads until the user taps play: first a thumbnail with a real
// play button, then -- only after that tap -- the provider's player in an
// iframe. That keeps the page free of third-party trackers and autoplay
// audio by default, and needs no native webview dependency.
//
// `playerUrl` is built by embedPlayerUrl, which re-validates provider and id;
// this component never sees an unvalidated one. The iframe is sandboxed to
// what a player needs (scripts, same-origin for its own storage, presentation,
// popups for its "watch on" link).
export function VideoEmbedCard({
  attachment,
  playerUrl,
  width,
}: {
  attachment: Attachment;
  playerUrl: string;
  width?: number;
}) {
  const [playing, setPlaying] = useState(false);
  const [playFocused, setPlayFocused] = useState(false);
  const colors = useColorScheme() === 'dark' ? darkColors : lightColors;

  const domain = attachment.domain ?? attachment.url;
  const title = attachment.title ?? domain;

  return (
    <YStack
      backgroundColor="$card"
      borderWidth={borderWidth.hairline}
      borderColor="$borderColor"
      borderRadius="$lg"
      overflow="hidden"
      alignSelf="stretch"
      maxWidth={width ? '100%' : 480}
      width={width}
    >
      {playing ? (
        // Letterbox black behind a video is intentional, not a theme color.
        <View aspectRatio={16 / 9} width="100%" backgroundColor="black">
          {createElement('iframe', {
            // The play button that had focus is gone; hand focus to the player.
            ref: (element: HTMLIFrameElement | null) => element?.focus(),
            src: playerUrl,
            title: `Video: ${title}`,
            allow: 'autoplay; encrypted-media; picture-in-picture; fullscreen',
            allowFullScreen: true,
            referrerPolicy: 'strict-origin-when-cross-origin',
            sandbox: 'allow-scripts allow-same-origin allow-presentation allow-popups',
            style: { border: 0, width: '100%', height: '100%' },
          })}
        </View>
      ) : (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Play video: ${title}`}
          accessibilityHint="Loads the video player"
          onPress={(event) => {
            event.stopPropagation();
            setPlaying(true);
          }}
          onFocus={() => setPlayFocused(true)}
          onBlur={() => setPlayFocused(false)}
          style={({ pressed }) => [
            { opacity: pressed ? 0.85 : 1 },
            playFocused
              ? { outlineStyle: 'solid', outlineWidth: 2, outlineColor: colors.actionPrimary, outlineOffset: -2 }
              : null,
          ]}
        >
          <View aspectRatio={16 / 9} width="100%" backgroundColor="$card" alignItems="center" justifyContent="center">
            {attachment.imageUrl && (
              <View position="absolute" top={0} left={0} right={0} bottom={0}>
                <AuthenticatedImage
                  path={attachment.imageUrl}
                  accessibilityLabel=""
                  width="100%"
                  aspectRatio={16 / 9}
                  objectFit="cover"
                />
              </View>
            )}
            <View
              width={56}
              height={56}
              borderRadius="$full"
              backgroundColor="$primary"
              alignItems="center"
              justifyContent="center"
            >
              <Play size={24} strokeWidth={1.75} color={colors.textOnAction} fill={colors.textOnAction} />
            </View>
          </View>
        </Pressable>
      )}
      <YStack gap="$1" p="$3">
        <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" numberOfLines={1}>
          {domain}
        </Text>
        <Text color="$color" fontWeight="$semibold" numberOfLines={2}>
          {title}
        </Text>
        {/* A way out that always works, if the provider blocks the embed. */}
        <Pressable
          accessibilityRole="link"
          accessibilityLabel="Open the video in your browser"
          {...hit(8)}
          onPress={(event) => {
            event.stopPropagation();
            if (/^https?:\/\//i.test(attachment.url)) void Linking.openURL(attachment.url);
          }}
        >
          <Text color="$colorLink" fontSize="$meta" lineHeight="$meta" textDecorationLine="underline">
            Open in browser
          </Text>
        </Pressable>
      </YStack>
    </YStack>
  );
}
